import React, { useState, useEffect, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldAlert, ShieldX, Users, GitMerge, Ban, Mail, Check, X, Loader2 } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const KIND_META = {
  fraud_attempt: { icon: ShieldX, tone: "text-red-500", key: "alerts.kind.fraud" },
  possible_duplicate: { icon: Users, tone: "text-amber-500", key: "alerts.kind.duplicate" },
  blacklist_use: { icon: Ban, tone: "text-amber-500", key: "alerts.kind.blacklist" },
};

export default function Alerts() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);   // {title, body, onOk}

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const r = await api.get(`/api/alerts/?status=${st}`);
      setRows(r.data.results || []);
      setOpenCount(r.data.open_count || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(status); }, [load, status]);

  const act = async (id, payload) => {
    try { await api.post(`/api/alerts/${id}/action/`, payload); load(status); }
    catch (e) { setConfirm({ title: t("alerts.error", "Erro"), body: e?.response?.data?.detail || String(e), onOk: null }); }
  };

  if (user && !user.capabilities?.manage_system) return <Navigate to="/liveness/sessions" replace />;

  const TABS = [["open", t("alerts.tab.open", "Abertos")], ["dismissed", t("alerts.tab.dismissed", "Descartados")],
    ["resolved", t("alerts.tab.resolved", "Resolvidos")], ["all", t("alerts.tab.all", "Todos")]];

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center gap-2 mb-1">
        <ShieldAlert className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">{t("alerts.title", "Alertas de segurança")}</h1>
        {openCount > 0 && <span className="ml-1 rounded-full bg-red-500/15 text-red-500 text-xs font-semibold px-2 py-0.5">{openCount}</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-5">{t("alerts.subtitle", "Tentativas de fraude por conta e possíveis biometrias duplicadas.")}</p>

      <div className="flex gap-1 mb-4">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setStatus(k)}
            className={`px-3 py-1.5 rounded-lg text-sm ${status === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t("alerts.empty", "Nenhum alerta.")}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => {
            const meta = KIND_META[a.kind] || KIND_META.fraud_attempt;
            const Icon = meta.icon;
            return (
              <Card key={a.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${meta.tone}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{a.title}</span>
                        {a.count > 1 && <span className="text-xs rounded-full bg-muted px-2 py-0.5">×{a.count}</span>}
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t(meta.key, a.kind)}</span>
                        {a.status !== "open" && <span className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{a.status}</span>}
                      </div>
                      {a.detail && <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>}
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                        {a.account && <span>{t("alerts.account", "Conta")}: {a.account.email}</span>}
                        {a.person && <Link className="text-primary hover:underline" to={`/liveness/admin/biometrics/${a.person.id}`}>{a.person.name || a.person.id.slice(0, 8)}</Link>}
                        {a.related_person && <>×<Link className="text-primary hover:underline" to={`/liveness/admin/biometrics/${a.related_person.id}`}>{a.related_person.name || a.related_person.id.slice(0, 8)}</Link></>}
                        <span>{a.created.slice(0, 19).replace("T", " ")}</span>
                      </div>

                      {a.status === "open" && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {a.kind === "fraud_attempt" && a.account && (
                            <>
                              <Button size="sm" variant="outline" className="text-red-500 border-red-500/40 hover:bg-red-500/10"
                                onClick={() => setConfirm({ title: t("alerts.blockTitle", "Bloquear conta"),
                                  body: t("alerts.blockBody", "A conta será desativada e não poderá mais acessar. Confirmar?"),
                                  onOk: () => act(a.id, { action: "block_account" }) })}>
                                <Ban className="h-4 w-4 mr-1" />{t("alerts.block", "Bloquear conta")}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => act(a.id, { action: "notify_owner" })}>
                                <Mail className="h-4 w-4 mr-1" />{t("alerts.notify", "Avisar proprietário")}
                              </Button>
                            </>
                          )}
                          {a.kind === "possible_duplicate" && a.person && a.related_person && (
                            <>
                              <Button size="sm" variant="outline"
                                onClick={() => setConfirm({ title: t("alerts.mergeTitle", "Mesclar biometrias"),
                                  body: t("alerts.mergeKeepA", "Manter {a} e remover {b}?").replace("{a}", a.person.name || a.person.id.slice(0, 8)).replace("{b}", a.related_person.name || a.related_person.id.slice(0, 8)),
                                  onOk: () => act(a.id, { action: "merge", keep: a.person.id, drop: a.related_person.id }) })}>
                                <GitMerge className="h-4 w-4 mr-1" />{t("alerts.mergeKeep", "Manter")} {a.person.name || a.person.id.slice(0, 8)}
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => setConfirm({ title: t("alerts.mergeTitle", "Mesclar biometrias"),
                                  body: t("alerts.mergeKeepA", "Manter {a} e remover {b}?").replace("{a}", a.related_person.name || a.related_person.id.slice(0, 8)).replace("{b}", a.person.name || a.person.id.slice(0, 8)),
                                  onOk: () => act(a.id, { action: "merge", keep: a.related_person.id, drop: a.person.id }) })}>
                                <GitMerge className="h-4 w-4 mr-1" />{t("alerts.mergeKeep", "Manter")} {a.related_person.name || a.related_person.id.slice(0, 8)}
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="outline" onClick={() => act(a.id, { action: "dismiss" })}>
                            <Check className="h-4 w-4 mr-1" />{t("alerts.dismiss", "Descartar")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl p-5">
            <h2 className="font-semibold mb-2">{confirm.title}</h2>
            <p className="text-sm text-muted-foreground mb-4">{confirm.body}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirm(null)}>{t("alerts.cancel", "Cancelar")}</Button>
              {confirm.onOk && <Button size="sm" onClick={() => { confirm.onOk(); setConfirm(null); }}>{t("alerts.confirm", "Confirmar")}</Button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
